import React, { useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventContentArg, DatesSetArg } from '@fullcalendar/core';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { TaskItem } from 'src/modules/shared/components/molecules/tasks/task-item';
import { isTaskOverdue } from 'src/modules/shared/utils/tasks/date.util';
import type { Task } from 'src/api/generated/models/Task';
import { TaskStatus } from 'src/api/generated/models/TaskStatus';
import { CalendarTaskItem } from '../../molecules/calendar-task-item';
import './styles.css';

interface TasksCalendarProps {
  tasks: Task[];
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

export const TasksCalendar: React.FC<TasksCalendarProps> = ({
  tasks,
  onDateRangeChange,
}) => {
  const [taskModalOpen, setTaskModalOpen] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState<Task | null>(null);
  const tasksById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks]
  );

  // Transform tasks into FullCalendar events
  const events = useMemo(() => {
    const dateTasks = tasks.filter(
      (task) => task.dueDate && task.dueDate.trim() !== ''
    );

    return dateTasks.map((task) => {
      const isCompleted = task.status === TaskStatus.COMPLETED;
      const isCancelled = task.status === TaskStatus.CANCELLED;
      const isOverdue = isTaskOverdue(task);

      let statusClass = 'task-scheduled';
      if (isCompleted) {
        statusClass = 'task-completed';
      } else if (isCancelled) {
        statusClass = 'task-cancelled';
      } else if (isOverdue) {
        statusClass = 'task-overdue';
      }

      return {
        id: task.id,
        // TODO: check when implementing i18n
        title: task.objective || 'Tarea sin título',
        start: task.dueDate!,
        extendedProps: {
          task,
          isCompleted,
          isCancelled,
          isOverdue,
        },
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        textColor: '#09090A',
        classNames: ['task-event', statusClass],
      };
    });
  }, [tasks]);

  const handleOpenTaskModal = (task: Task) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setTaskModalOpen(false);
    setSelectedTask(null);
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    const task = tasksById.get(eventInfo.event.id);
    if (!task) return null;

    return (
      <CalendarTaskItem
        task={task}
        onOpenModal={() => handleOpenTaskModal(task)}
      />
    );
  };

  const handleDatesSet = (dateInfo: DatesSetArg) => {
    if (!onDateRangeChange) return;

    onDateRangeChange(dateInfo.start, dateInfo.end);
  };

  return (
    <>
      <div className="h-full p-4 bg-white rounded-lg shadow-md overflow-visible">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          height="100%"
          events={events}
          eventContent={renderEventContent}
          selectable={false}
          dayMaxEvents={true}
          // View-specific configurations
          views={{
            dayGridMonth: {
              eventMaxStack: 3,
              moreLinkClick: 'day',
            },
            timeGridWeek: {
              eventMaxStack: 2,
              moreLinkClick: 'day',
            },
            timeGridDay: {
              eventMaxStack: 4,
              moreLinkClick: 'popover',
            },
          }}
          eventDisplay="block"
          displayEventTime={true}
          allDaySlot={false}
          // Week view configuration to prevent overlapping
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          slotEventOverlap={false}
          eventOrderStrict={true}
          // Allow expanding events to prevent overlap
          expandRows={true}
          // Ensure proper event spacing for more links
          eventMinHeight={20}
          locale="es"
          buttonText={{
            // TODO: check when implementing i18n
            today: 'Hoy',
            month: 'Mes',
            week: 'Semana',
            day: 'Día',
          }}
          dayHeaderFormat={{ weekday: 'short' }}
          eventClassNames={(eventInfo) => {
            const { isCompleted, isCancelled } = eventInfo.event.extendedProps;
            const baseClasses = [
              'cursor-pointer',
              'hover:opacity-80',
              'transition-opacity',
              'duration-200',
            ];

            if (isCompleted || isCancelled) {
              baseClasses.push('opacity-60');
            }

            return baseClasses;
          }}
          datesSet={handleDatesSet}
        />
      </div>

      {selectedTask && (
        <Modal
          isOpen={taskModalOpen}
          onClose={handleCloseTaskModal}
          header={
            <Text size="h6" weight="medium" className="text-primary-800">
              {/* TODO: check when implementing i18n */}
              Detalles de la tarea
            </Text>
          }
          body={
            <div className="w-full">
              <TaskItem
                task={selectedTask}
                showConversationLink={!!selectedTask.context.conversationId}
              />
            </div>
          }
          dialogClassName="!max-w-none sm:!max-w-7xl w-full mx-4"
          bodyClassName="!px-4 !py-6"
        />
      )}
    </>
  );
};
